import { useState } from "react";
import { format } from "date-fns";
import { LoaderCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { DateField } from "@/components/form/DateField";
import { FormField } from "@/components/form/FormField";
import { api, formatApiError } from "@/lib/api";
import { STATUS_OPTIONS, slugify } from "@/lib/constants";

const channel = (on, label) => `${label} ${on ? "aktif" : "belum aktif"}`;

export const AddProgressForm = ({ complaint, notifyStatus, onAdded }) => {
  const [tanggal, setTanggal] = useState(new Date());
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState("keep");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return toast.error("Respon penyelesaian wajib diisi");
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/complaints/${complaint.id}/progress`, {
        tanggal: format(tanggal, "yyyy-MM-dd"), message: message.trim(), status: status === "keep" ? null : status,
      });
      setMessage("");
      setStatus("keep");
      toast.success("Progres penyelesaian ditambahkan", { description: "Notifikasi dikirim ke warga bila kanal aktif." });
      onAdded(data);
    } catch (err) {
      toast.error(formatApiError(err, "Gagal menyimpan progres"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4 rounded-2xl border border-emerald-200 bg-emerald-50/40 p-4 sm:p-5" data-testid="add-progress-form">
      <h4 className="font-heading text-base font-bold text-slate-900">Tambah Progres Penyelesaian</h4>
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField id="progress-date" label="Tanggal Progres" required>
          <DateField id="progress-date" testId="progress-date" value={tanggal} onChange={setTanggal} allowFuture />
        </FormField>
        <FormField id="progress-status" label="Ubah Status (opsional)">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger id="progress-status" className="h-11 rounded-xl border-slate-200 bg-white" data-testid="progress-status-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="keep" data-testid="progress-status-option-keep">Tidak diubah ({complaint.status})</SelectItem>
              {STATUS_OPTIONS.map((s) => (
                <SelectItem key={s} value={s} data-testid={`progress-status-option-${slugify(s)}`}>{s}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </FormField>
      </div>
      <FormField id="progress-message" label="Respon / Keterangan Penyelesaian" required>
        <Textarea id="progress-message" rows={4} maxLength={3000} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Contoh: Petugas kebersihan sudah dijadwalkan mengangkut sampah besok pagi." className="rounded-xl border-slate-200 bg-white text-sm focus-visible:ring-2 focus-visible:ring-emerald-500/30" data-testid="progress-message-input" />
      </FormField>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-slate-500" data-testid="progress-notify-info">
          Notifikasi ke warga: {notifyStatus ? `${channel(notifyStatus.email, "Email")} · ${channel(notifyStatus.whatsapp, "WhatsApp")}` : "memeriksa..."}
        </p>
        <Button type="submit" disabled={saving} className="rounded-xl bg-emerald-600 hover:bg-emerald-700" data-testid="progress-submit-btn">
          {saving ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Simpan Progres
        </Button>
      </div>
    </form>
  );
};
